import { useEffect, useRef } from "react";

/**
 * Escape closes the top-most overlay only.
 *
 * Handlers register on a module stack in mount order. One document listener
 * dispatches Escape to the last registered handler. Events that a nested
 * control already `preventDefault()`ed are ignored, so a picker inside a
 * dialog can consume Escape without closing the dialog.
 */

type EscapeHandler = () => void;

const stack: { current: EscapeHandler }[] = [];
let listening = false;

function onDocumentKeyDown(event: globalThis.KeyboardEvent): void {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    const top = stack[stack.length - 1];
    if (top === undefined) return;
    event.preventDefault();
    top.current();
}

function ensureListener(): void {
    if (listening || typeof document === "undefined") return;
    document.addEventListener("keydown", onDocumentKeyDown);
    listening = true;
}

function releaseListener(): void {
    if (!listening || stack.length > 0) return;
    document.removeEventListener("keydown", onDocumentKeyDown);
    listening = false;
}

export function useEscapeKey(active: boolean, handler: EscapeHandler): void {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!active) return;
        const entry = { current: () => handlerRef.current() };
        stack.push(entry);
        ensureListener();
        return () => {
            const index = stack.indexOf(entry);
            if (index >= 0) stack.splice(index, 1);
            releaseListener();
        };
    }, [active]);
}
